import React, { useState } from 'react';
import { Typography, Box, Avatar, Button } from '@mui/material';
import { Circle as CircleIcon, Biotech as LabIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { refreshMyLabs } from 'shared/hooks/useMyLabs';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';

/** Lab activity for you: an invitation to join (accept / decline), an answer to one you sent,
 *  an application (PI), a team-chat message (members). */
export default function LabNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const p = notification.payload || {};
  const [broken, setBroken] = useState(false);
  const [answer, setAnswer] = useState(null);       // 'accepted' | 'declined' after answering here
  const [error, setError] = useState(null);
  const avatar = !broken && (p.from_user_avatar_url || null);

  const open = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (!p.lab_id) return;
    const q = p.kind === 'chat' ? '?panel=chat' : p.kind === 'application' && p.position_id ? `?applicants=${p.position_id}` : '';
    navigate(`/lab/${p.lab_id}/${q}`);
  };
  const respond = async (e, choice) => {
    e.stopPropagation();
    setError(null);
    try {
      await axiosService.post(`/consortium/labs/${p.lab_id}/invitation/${choice}/`);
      if (choice === 'accept') refreshMyLabs();         // the lab appears in the sidebar
      setAnswer(choice === 'accept' ? 'accepted' : 'declined');
      if (!notification.is_read && onMarkRead) onMarkRead();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not answer the invitation.'));
    }
  };
  const action = {
    application: <> applied for <Box component="span" fontWeight={600}>{p.position_title || 'a position'}</Box> in </>,
    chat: <> wrote in the team chat of </>,
    invitation: p.as_alumni ? <> invited you to be listed among the alumni of </> : <> invited you to join </>,
    invitation_accepted: <> accepted your invitation to </>,
    listing_linked: p.as_alumni ? <> listed you among the alumni of </> : <> listed you as a member of </>,
    invitation_declined: <> declined your invitation to </>,
    contribution_submitted: <> added a {p.item_kind || 'contribution'} for you to confirm on </>,
    contribution_approved: <> approved your {p.item_kind || 'contribution'} on </>,
    contribution_rejected: <> did not approve your {p.item_kind || 'contribution'} on </>,
  }[p.kind] || <> added you to </>;

  return (
    <Box
      onClick={open}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer', '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar src={avatar || undefined} onError={() => setBroken(true)}
        sx={{ width: 34, height: 34, mt: 0.25, fontSize: 13, bgcolor: theme.palette.primary.avatar, color: 'white' }}>
        <LabIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          <Box component="span" fontWeight={600}>{p.from_user_name || 'Someone'}</Box>
          <Box component="span" color="text.secondary">{action}</Box>
          <Box component="span" fontWeight={600}>{p.lab_name || 'a lab'}</Box>
          {['member_added', 'invitation'].includes(p.kind) && p.lab_title && <Box component="span" color="text.secondary"> as {p.lab_title}</Box>}
          {p.kind === 'chat' && Number(p.count) > 1 && <Box component="span" color="text.secondary"> · {p.count} messages</Box>}
        </Typography>
        {p.kind?.startsWith('contribution_') && p.item_title && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>“{p.item_title}”</Typography>
        )}
        {p.kind === 'chat' && p.preview && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>“{p.preview}”</Typography>
        )}
        {p.kind === 'invitation' && (
          answer ? (
            <Typography variant="caption" color={answer === 'accepted' ? 'success.main' : 'text.secondary'} display="block" sx={{ mt: 0.5 }}>
              {answer === 'accepted' ? (p.as_alumni ? 'You are listed among the alumni.' : 'You joined the lab.') : 'You declined the invitation.'}
            </Typography>
          ) : (
            <Box display="flex" gap={1} mt={0.75}>
              <Button size="small" variant="contained" onClick={(e) => respond(e, 'accept')}>Accept</Button>
              <Button size="small" variant="outlined" color="inherit" onClick={(e) => respond(e, 'decline')}>Decline</Button>
            </Box>
          )
        )}
        {error && <Typography variant="caption" color="error" display="block">{error}</Typography>}
        {!['chat', 'invitation'].includes(p.kind) && p.organization_name && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>{p.organization_name}</Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
